import { apiClient } from '../lib/apiClient';
import { approvalStateOfCode, type AccountApprovalState } from '../lib/accountApproval';

export interface AuthTokens {
  accessToken: string;
  idToken: string;
  refreshToken?: string;
}

export interface MfaChallenge {
  session: string;
  challengeName: string;
  /** Cognito username from the challenge (USER_ID_FOR_SRP); sent back with the code. */
  username?: string;
}

/**
 * Closed beta: Cognito accepted the password, but the API refused the session
 * (403 ACCOUNT_PENDING_APPROVAL / ACCOUNT_REJECTED) — the account still waits for
 * an admin's approval, or was not approved.
 */
export interface ApprovalRequiredResult {
  type: 'approval_required';
  state: AccountApprovalState;
}

export type LoginResult =
  | { type: 'success'; tokens: AuthTokens; user: { email: string; userId: string } }
  | { type: 'mfa_required'; challenge: MfaChallenge }
  | ApprovalRequiredResult
  | { type: 'error'; message: string };

export type MfaConfirmResult =
  | { type: 'success'; tokens: AuthTokens; user: { email: string; userId: string } }
  | ApprovalRequiredResult
  /** `invalid_code`: wrong code; `expired`: the sign-in session ran out (start again). */
  | { type: 'error'; message: string; reason?: 'invalid_code' | 'expired' };

/** An ApiError 403 ACCOUNT_PENDING_APPROVAL / ACCOUNT_REJECTED (checked by shape, not by class). */
function approvalRequired(err: unknown): ApprovalRequiredResult | null {
  if (typeof err !== 'object' || err === null) return null;
  const { status, code } = err as { status?: unknown; code?: unknown };
  if (status !== 403 || typeof code !== 'string') return null;
  const state = approvalStateOfCode(code);
  return state === null ? null : { type: 'approval_required', state };
}

export async function login(email: string, password: string): Promise<LoginResult> {
  try {
    const data = await apiClient.post<Record<string, unknown>>('/api/auth/login', { email, password });

    // The backend wraps its response in a `data` envelope.
    const body = data as Record<string, unknown>;
    const payload = (body['data'] as Record<string, unknown>) ?? body;

    // MFA challenge — backend returns challengeName + session at the top level of data
    if (payload['challengeName'] !== undefined) {
      const params = payload['challengeParameters'];
      const username =
        params && typeof params === 'object' && typeof (params as Record<string, unknown>)['USER_ID_FOR_SRP'] === 'string'
          ? ((params as Record<string, unknown>)['USER_ID_FOR_SRP'] as string)
          : undefined;
      return {
        type: 'mfa_required',
        challenge: {
          session: String(payload['session'] ?? ''),
          challengeName: String(payload['challengeName'] ?? ''),
          ...(username ? { username } : {}),
        },
      };
    }

    // Successful auth — backend returns tokens flat (no nested `tokens` wrapper)
    const accessToken = String(payload['accessToken'] ?? '');
    const idToken = String(payload['idToken'] ?? '');
    const refreshToken = payload['refreshToken'] !== undefined ? String(payload['refreshToken']) : undefined;

    if (!accessToken || !idToken) {
      return { type: 'error', message: 'Authentication failed. Please try again.' };
    }

    // Decode the idToken payload (base64url) to extract email and sub
    let userEmail = email;
    let userId = '';
    try {
      const jwtPayload = JSON.parse(atob(idToken.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))) as Record<string, unknown>;
      userEmail = typeof jwtPayload['email'] === 'string' ? jwtPayload['email'] : email;
      userId = typeof jwtPayload['sub'] === 'string' ? jwtPayload['sub'] : '';
    } catch {
      // JWT decode failed — fall back to email from input
    }

    return {
      type: 'success',
      tokens: { accessToken, idToken, refreshToken },
      user: { email: userEmail, userId },
    };
  } catch (err: unknown) {
    return approvalRequired(err) ?? { type: 'error', message: 'Authentication failed. Please try again.' };
  }
}

export async function mfaConfirm(
  email: string,
  challenge: MfaChallenge,
  code: string,
): Promise<MfaConfirmResult> {
  try {
    const data = await apiClient.post<Record<string, unknown>>('/api/auth/login/mfa-confirm', {
      email,
      session: challenge.session,
      challengeName: challenge.challengeName,
      code,
      ...(challenge.username ? { username: challenge.username } : {}),
    });

    const body = data as Record<string, unknown>;
    const payload = (body['data'] as Record<string, unknown>) ?? body;

    const accessToken = String(payload['accessToken'] ?? '');
    const idToken = String(payload['idToken'] ?? '');
    const refreshToken = payload['refreshToken'] !== undefined ? String(payload['refreshToken']) : undefined;

    if (!accessToken || !idToken) {
      return { type: 'error', message: 'Authentication failed. Please try again.' };
    }

    let userEmail = email;
    let userId = '';
    try {
      const jwtPayload = JSON.parse(atob(idToken.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))) as Record<string, unknown>;
      userEmail = typeof jwtPayload['email'] === 'string' ? jwtPayload['email'] : email;
      userId = typeof jwtPayload['sub'] === 'string' ? jwtPayload['sub'] : '';
    } catch {
      // JWT decode failed — fall back to email from input
    }

    return {
      type: 'success',
      tokens: { accessToken, idToken, refreshToken },
      user: { email: userEmail, userId },
    };
  } catch (err: unknown) {
    const approval = approvalRequired(err);
    if (approval) return approval;
    const code = typeof err === 'object' && err !== null ? (err as { code?: unknown }).code : undefined;
    if (code === 'CODE_MISMATCH') return { type: 'error', message: 'Invalid code.', reason: 'invalid_code' };
    if (code === 'NOT_AUTHORIZED') return { type: 'error', message: 'Session expired.', reason: 'expired' };
    return { type: 'error', message: 'Authentication failed. Please try again.' };
  }
}

/**
 * POST /api/auth/logout — the backend revokes the refresh token with Cognito
 * (RevokeToken also invalidates the access/ID tokens issued from it).
 */
export async function logout(refreshToken?: string): Promise<void> {
  try {
    await apiClient.post('/api/auth/logout', refreshToken ? { refreshToken } : {});
  } catch {
    // Logout is best-effort — clear local state regardless of server response
  }
}
